import { useLocation, useNavigate } from "react-router-dom";
import {
  chevronLeft,
  threeDotsIcon,
  plusIcon,
  infoCircle,
} from "../../../utility-functions/assetLoader";
import classes from "./CreateTemplate.module.css";
import {
  CustomInput,
  CustomSelect,
  CustomTooltip,
} from "../../design/components/customComponents";
import { useState, useRef, useEffect, useMemo } from "react";
import GBottomSaveBar from "../../design/components/GBottomSaveBar";
import { JService } from "../../api-service/ApiService";
import { enqueueSnackbar } from "notistack";
import Tag from "../Components/Tag";
import { Menu, MenuItem } from "@mui/material";
import DeleteTemplateModal from "../Modals/DeleteTemplateModal";

const allCategories = [
  { value: "MARKETING", displayValue: "Marketing" },
  { value: "UTILITY", displayValue: "Utility" },
  { value: "AUTHENTICATION", displayValue: "Authentication" },
];

const CreateTemplate = () => {
  const navigate = useNavigate();
  const textareaRef = useRef(null);
  const location = useLocation();

  const oldTemplateName = location?.state?.name;
  const oldCategory = location?.state?.category?.toUpperCase();
  const oldComponents = location?.state?.components || [];
  const oldBodyComponent = oldComponents?.find(
    (component) => component?.type?.toUpperCase() === "BODY"
  );
  const oldFooterComponent = oldComponents?.find(
    (component) => component?.type?.toUpperCase() === "FOOTER"
  );
  const oldButtonsComponent = oldComponents?.find(
    (component) => component?.type?.toUpperCase() === "BUTTONS"
  );
  const oldBodyText = oldBodyComponent?.text;
  const oldSampleValues = oldBodyComponent?.example?.body_text?.[0];
  const oldCodeExpirationMinutes = oldFooterComponent?.code_expiration_minutes;
  const oldAddSecurityRecommendation = oldBodyComponent?.add_security_recommendation ?? true;
  const oldCopyCodeText = oldButtonsComponent?.buttons?.[0]?.text;
  // States
  const [templateName, setTemplateName] = useState({
    value: oldTemplateName ?? "",
    error: "",
  });

  const [codeExpirationMinutes, setCodeExpirationMinutes] = useState(
    oldCodeExpirationMinutes ?? "10"
  );
  const [copyCodeText, setCopyCodeText] = useState(
    oldCopyCodeText ?? "Copy Code"
  );
  const [addSecurityRecommendation, setAddSecurityRecommendation] = useState(
    oldAddSecurityRecommendation ?? true
  );
  const [category, setCategory] = useState(oldCategory);
  const [bodyText, setBodyText] = useState(oldBodyText ?? "");
  const [sampleValues, setSampleValues] = useState(oldSampleValues ?? []);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isAuthentication = category === "AUTHENTICATION";
  const authPreviewCode = "<OTP>";
  const authPreviewExpiry = Number(codeExpirationMinutes || 10);
  const authPreviewCopyText = copyCodeText?.trim() || "Copy Code";
  const expiryLabel = authPreviewExpiry === 1 ? "minute" : "minutes";

  const isSubmitDisabled =
    !templateName.value.trim() ||
    !category ||
    (isAuthentication
      ? !codeExpirationMinutes || !copyCodeText.trim()
      : !bodyText.trim() || sampleValues.some((val) => !val.trim()));

  const isSubmitDisabledOnUpdate = useMemo(() => {
    if (isAuthentication) {
      return (
        category === oldCategory &&
        Number(codeExpirationMinutes) === Number(oldCodeExpirationMinutes) &&
        copyCodeText.trim() === (oldCopyCodeText || "").trim() &&
        Boolean(addSecurityRecommendation) === Boolean(oldAddSecurityRecommendation)
      );
    }
    return (
      category === oldCategory &&
      bodyText.trim() === oldBodyText?.trim() &&
      sampleValues.every(
        (val, idx) => val.trim() === oldSampleValues?.[idx]?.trim()
      )
    );
  }, [isAuthentication, category, bodyText,
    sampleValues, codeExpirationMinutes,
    copyCodeText, addSecurityRecommendation,
    oldCodeExpirationMinutes, oldCopyCodeText,
    oldAddSecurityRecommendation
  ]);

  //Handlers
  const dropdownClickHandler = () => {
    setShowDeleteModal(true);
    setIsDropdownVisible(false);
    setDropdownAnchorEl(null);
  };

  const threeDotsAutoClose = (e) => {
    const path = e.composedPath()?.map((pathItem) => {
      let newPathItem = null;
      newPathItem =
        pathItem?.tagName?.toLowerCase() +
        (pathItem?.className ? "." + pathItem?.className : "");
      return newPathItem;
    });

    if (!path?.length || path[0]?.includes("div.MuiBackdrop-root")) {
      setIsDropdownVisible(false);
      setDropdownAnchorEl(null);
      return;
    }
  };

  const threeDotsClickHandler = (e) => {
    e.stopPropagation();
    setIsDropdownVisible(true);
    setDropdownAnchorEl(e.currentTarget || null);
  };

  const processTextChange = (rawValue) => {
    const MAX_VARIABLES = 10;

    const oldText = bodyText;
    const oldMatches = Array.from(oldText.matchAll(/{{\d+}}/g), (m) => m[0]);
    const newMatchesRaw = Array.from(rawValue.matchAll(/{{\d+}}/g), (m) => m[0]);

    let updatedSamples = [...sampleValues];

    const oldCount = oldMatches.length;
    let newCount = newMatchesRaw.length;

    // Enforce max limit
    if (newCount > MAX_VARIABLES) {
      newCount = MAX_VARIABLES;
    }

    // Trim excess new matches if user added more than allowed
    const trimmedMatches = newMatchesRaw.slice(0, MAX_VARIABLES);

    if (newCount < oldCount) {
      const diff = oldCount - newCount;
      updatedSamples.splice(-diff, diff); // remove last `diff` values
    } else if (newCount > oldCount) {
      const additions = newCount - oldCount;
      updatedSamples = updatedSamples.concat(Array(additions).fill(""));
    }

    // Renumber only allowed (trimmed) number of placeholders
    let count = 0;
    const renumbered = rawValue.replace(/{{\d+}}/g, () => {
      if (count < MAX_VARIABLES) {
        return `{{${++count}}}`;
      }
      enqueueSnackbar("Maximum of 10 variables allowed.", { variant: "error" });
      return ""; // remove extra placeholders beyond limit
    });

    setSampleValues(updatedSamples);
    setBodyText(renumbered);
  };

  const bodyChangeHandler = (e) => {
    processTextChange(e.target.value);
  };

  const keyDownHandler = (e) => {
    if (!textareaRef.current) return;
    const { selectionStart: start, selectionEnd: end } = textareaRef.current;
    if (start !== end) return;
    const key = e.key;
    if (key !== "Backspace" && key !== "Delete") return;

    const text = bodyText;
    const matches = Array.from(text.matchAll(/{{\d+}}/g));

    for (let m of matches) {
      const tag = m[0];
      const tagStart = m.index;
      const tagEnd = tagStart + tag.length;

      if (
        (key === "Backspace" && start > tagStart && start <= tagEnd) ||
        (key === "Delete" && start >= tagStart && start < tagEnd)
      ) {
        e.preventDefault();
        const newRaw = text.slice(0, tagStart) + text.slice(tagEnd);
        processTextChange(newRaw);
        setTimeout(() => {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(tagStart, tagStart);
        }, 0);
        return;
      }
    }
  };

  const addVariableHandler = () => {
    if (sampleValues.length >= 10) {
      enqueueSnackbar("Maximum of 10 variables allowed.", { variant: "error" });
      return;
    }
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      const newIndex = sampleValues.length + 1;
      const tag = `{{${newIndex}}}`;

      const newRaw = bodyText.slice(0, start) + tag + bodyText.slice(end);
      processTextChange(newRaw);

      setTimeout(() => {
        const pos = start + tag.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }, 0);
    }
  };

  const sampleChangeHandler = (index, value) => {
    setSampleValues((prev) => {
      const copy = [...prev];
      copy[index] = value;
      return copy;
    });
  };

  const cancelHandler = () => {
    setTemplateName({ value: "", error: "" });
    setCategory("");
    setBodyText("");
    setCodeExpirationMinutes("");
    setCopyCodeText("Copy Code");
    setAddSecurityRecommendation(true);
    setSampleValues([]);
    navigate(-1);
  };

  const submitTemplateHandler = async () => {
    const authenticationComponents = [
      {
        type: "BODY",
        add_security_recommendation: Boolean(addSecurityRecommendation),
      },
      {
        type: "FOOTER",
        code_expiration_minutes: Number(codeExpirationMinutes),
      },
      {
        type: "BUTTONS",
        buttons: [
          {
            type: "OTP",
            otp_type: "COPY_CODE",
            text: copyCodeText.trim(),
          },
        ],
      },
    ];

    const defaultComponents = [
      {
        type: "BODY",
        text: bodyText,
      },
    ];

    const payload = location?.state?.edit
      ? {
        template_id: location?.state?.template_id,
        category: category,
        components: isAuthentication ? authenticationComponents : defaultComponents,
      }
      : {
        name: templateName.value,
        language: "en_US",
        category: category,
        components: isAuthentication ? authenticationComponents : defaultComponents,
      };

    if (!isAuthentication && sampleValues.length) {
      payload.components[0].example = {
        body_text: [[...sampleValues.map((val) => val.trim())]],
      };
    }

    try {
      setIsLoading(true);
      const response = location?.state?.edit
        ? await JService.patch("/api/v1/whatsapp/templates/", payload)
        : await JService.post("/api/v1/whatsapp/templates/", payload);
      if (!response?.success)
        throw new Error(response?.message || "Could not submit for review");
      enqueueSnackbar("Template sent for review.", { variant: "success" });
      navigate("/w-template");
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  //Effects
  useEffect(() => {
    if (isDropdownVisible) window.addEventListener("click", threeDotsAutoClose);
    else window.removeEventListener("click", threeDotsAutoClose);

    return () => {
      window.removeEventListener("click", threeDotsAutoClose);
    };
  }, [isDropdownVisible]);

  useEffect(() => {
    if (!isAuthentication) return;
    if (codeExpirationMinutes) setCodeExpirationMinutes("10");
    if (!addSecurityRecommendation) setAddSecurityRecommendation(true);
    if (copyCodeText.trim() !== "Copy Code") setCopyCodeText("Copy Code");
  }, [isAuthentication]);

  return (
    <div className="whatsapp-screen-container">
      <button
        onClick={() => navigate("/w-template")}
        className={classes.backBtn}
      >
        {chevronLeft()}Back to templates
      </button>
      <div className={classes.topHeader}>
        <div className={classes.headingContainer}>
          <h3>
            {location?.state?.edit
              ? location?.state?.name
              : "Create new template"}
          </h3>
          {location?.state?.edit && (
            <Tag
              quality={location?.state?.quality}
              name={location?.state?.status}
            />
          )}
        </div>
        {location?.state?.edit && (
          <>
            <div
              className={classes.threeDotsContainer}
              onClick={(e) => threeDotsClickHandler(e)}
            >
              {threeDotsIcon()}
            </div>
            <Menu
              sx={{
                "& .MuiList-root": { width: "175px" },
                "& .MuiPaper-root": { border: "1px solid var(--grey-200)" },
              }}
              open={isDropdownVisible}
              anchorEl={dropdownAnchorEl}
            >
              <MenuItem
                onClick={dropdownClickHandler}
                data-menuitemtype="delete"
                sx={{ cursor: "pointer" }}
              >
                &nbsp;&nbsp;Delete
              </MenuItem>
            </Menu>
            <DeleteTemplateModal
              visible={showDeleteModal}
              closeModalHandler={() => setShowDeleteModal(false)}
              name={location?.state?.name}
              id={location?.state?.template_id}
            />
          </>
        )}
      </div>
      <div className={classes.sep} />
      <div className={classes.secContainer}>
        <div className={classes.sec1}>
          <h4>Template</h4>
          <div className={classes.infoContainer}>
            <CustomInput
              label="Template name"
              placeholder="Enter name"
              type="text"
              labelStyles={{ color: "#3F3F46", width: "71.5%" }}
              value={templateName}
              handleChange={(e) =>
                setTemplateName((prev) => ({ ...prev, value: e.target.value }))
              }
              inputProps={{ disabled: location?.state?.edit, maxLength: 60 }}
            />
            <div style={{ flexGrow: 1 }}>
              <CustomSelect
                renderVal={(val) =>
                  val ? (
                    <span className={classes.categoryVal}>
                      {allCategories?.find((c) => c.value === val)?.displayValue}
                    </span>
                  ) : (
                    <span className={classes.defaultVal}>Select category</span>
                  )
                }
                label={
                  <div className={classes.categoryLabelContainer}>
                    <span>Category</span>
                    <CustomTooltip
                      placement="top"
                      tooltipTitle={
                        <div className={classes.whatsappTooltip}>
                          <p>
                            Categories help WhatsApp determine the purpose of the
                            message and ensure compliance with its policies.
                          </p>
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://developers.facebook.com/docs/whatsapp/updates-to-pricing/new-template-guidelines/"
                          >
                            Learn more
                          </a>
                        </div>
                      }
                    >
                      {infoCircle()}
                    </CustomTooltip>
                  </div>
                }
                labelStyles={{ color: "#3F3F46" }}
                value={category}
                handleChange={(e) => setCategory(e.target.value)}
                options={allCategories}
                selectStyles={{ height: "36.5px", marginTop: "3.5px" }}
              />
            </div>
          </div>
          {isAuthentication && (
            <div className={classes.authSection}>
              <h4>Authentication settings</h4>
              <CustomInput
                label="Code expiration (minutes)"
                placeholder="Enter minutes"
                type="number"
                value={{ value: codeExpirationMinutes, error: "" }}
                handleChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || Number(val) >= 1) setCodeExpirationMinutes(val);
                }}
                inputProps={{ min: 1 }}
                labelStyles={{ width: "100%" }}
              />
              <CustomInput
                label="Button"
                placeholder="Copy Code"
                value={{ value: copyCodeText, error: "" }}
                handleChange={() => {}}
                labelStyles={{ width: "100%" }}
                inputProps={{ readOnly: true }}
              />
              <label className={classes.checkboxRow}>
                <input
                  type="checkbox"
                  checked={addSecurityRecommendation}
                  onChange={(e) => setAddSecurityRecommendation(e.target.checked)}
                />
                <span>Add security recommendation</span>
              </label>
            </div>
          )}
          {!isAuthentication && (
            <>
            <h4>Content</h4>
              <label className={classes.body}>
                Body
                <textarea
                  rows={5}
                  placeholder="Enter text in English"
                  ref={textareaRef}
                  value={bodyText}
                  onChange={bodyChangeHandler}
                  onKeyDown={keyDownHandler}
                />
              </label>
              <button
                className={classes.addVariable}
                onClick={addVariableHandler}
                type="button"
              >
                {plusIcon("#3F3F46")} Add variable
              </button>

              {sampleValues.length > 0 && (
                <div className={classes.sampleSection}>
                  <h6>Sample values</h6>
                  <p>Please add an example for each variable in your body text.</p>
                  {sampleValues.map((val, idx) => (
                    <CustomInput
                      key={idx}
                      label={`{{${idx + 1}}}`}
                      placeholder={`Enter content for {{${idx + 1}}}`}
                      type="text"
                      value={{ value: val, error: "" }}
                      handleChange={(e) =>
                        sampleChangeHandler(idx, e.target.value)
                      }
                      labelStyles={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "12px",
                        width: "100%",
                        marginBottom: "12px",
                      }}
                      inputContainerStyle={{ width: "100%" }}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className={classes.sec2}>
          <h5>Template preview</h5>
          <div className={classes.previewBoxContainer}>
            {isAuthentication ? (
              <div className={classes.previewBox}>
                <p className={classes.authPreviewLine}>
                  <span className={classes.authPreviewCode}>
                    {authPreviewCode}
                  </span>{" "}
                  is your verification code.
                  {addSecurityRecommendation
                    ? " For your security, do not share this code."
                    : ""}
                </p>
                <p className={classes.authPreviewExpiry}>
                  This code expires in {authPreviewExpiry} {expiryLabel}.
                </p>
                <div className={classes.authPreviewButton}>
                  {authPreviewCopyText}
                </div>
              </div>
            ) : (
              bodyText && (
                <div className={classes.previewBox}>
                  <div
                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {bodyText}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
      <GBottomSaveBar
        onCancel={cancelHandler}
        show={true}
        secondBtnText="Submit for review"
        secondBtnDisabled={isSubmitDisabled || isSubmitDisabledOnUpdate}
        onSave={submitTemplateHandler}
        secondBtnLoading={isLoading}
      />
    </div>
  );
};

export default CreateTemplate;
